import { useEffect } from 'react';
import rtfToHTML from '@iarna/rtf-to-html';
import { trimNull } from './utils';
import { store } from '~/app/redux/index';
import { actions as returnProfileActions } from '~/app/redux/returnProfile/duck';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer';
import { actions as remoteSignActions } from '~/app/redux/remoteSign/duck';
import ErrorHelpers from '~/app/errorHelpers.js';
import XlinkAPI from '~/app/api/xlinkAPI';
import { sendSignatureRequest } from '~/app/Pages/Returns/pages/Signatures/signaturesHelper.js';
import { PRINT_TYPES, SIGNEE_TYPE, VERIFY_MODAL_TABS } from '~/app/constants';
import { translateSpecialOccuranceRow } from '~/app/Utility/general';

const TopOfFormPage = 0;

/**
 * useSocketMessage
 *
 * Owns the `handleSocketMessage` function that was previously a class method
 * on FormViewer. The handler is registered on `onMessageRef.current` inside a
 * useEffect so that useCalcSession always calls the latest version.
 *
 * All class-owned operations (setState, class methods, Redux props) are
 * accessed at call-time through `socketCallbacksRef.current`, which the class
 * populates in componentDidMount. This keeps the hook free of stale closures
 * while keeping the switch body readable.
 *
 * socketCallbacksRef.current shape:
 *
 *   // already-extracted hook objects (always current)
 *   spinner         — useSpinner result
 *   snackbars       — useSnackbars result
 *   formNav         — useFormNavigation result
 *   fieldEvents     — useFormFieldEvents result
 *   formPopulation  — useFormPopulation result
 *   verify          — useVerify result
 *   print           — usePrint result
 *   bookmarks       — useBookmarks result
 *   returnClose     — useReturnClose result
 *
 *   // class-state setters / getters
 *   setState(partial)
 *   getState()         → snapshot of class state
 *   setSessionId(id)
 *
 *   // class methods delegated as callbacks
 *   lockFieldsIfRequired()
 *   handleMenuCommand(cmd, response)
 *   handleSpecialFormLoadResponse(data)
 *   interviewMode()
 *   setVerifyType(type)
 *   getLatestArchivedDocuments()
 *   openAddAttachmentModal()
 *   closeQueueReturnModal()
 *   openQueueReturnModal()
 *   PopulateFormsAndActivitiesList(list)
 *   toggleAssetSnackbar(msg, toggle)
 *   setPaymentTerminalStatus(status)
 *   toggleFieldLock(locked)
 *   handleQueueReturn()
 *   handlePrintFinal()
 *   onUpdateFormError(response)
 *   handleFilingStatusError(error)
 *   copyToTraining()
 *   copyToLive()
 *   updateRestrictedFields(arg)
 *   signatureRequestData    (property — returns current value)
 *
 *   // Redux props passed through
 *   currentElRef
 *   returnInitializedRef
 *   returnID
 *   isSelfPreparing
 *   isSignatureRequestActive
 *   isRMSSigningMethod
 *   encryptedPdfSelected
 *   taxpayerChecked
 *   taxpayerEPasshecked
 *   spouseChecked
 *   spouseEPassChecked
 *   startInterview
 *   addFormPkg
 *   lockedStatus
 *   payload                 (JWT payload — stable)
 *   eroData
 *   preparerData
 *   amendedPreparerData
 *   initialSigneeData
 *   primarySSNEIN
 *   // action dispatchers
 *   needingVerifiedSet(v)
 *   openErrorRejectModal()
 *   toggleVerifyModalDialog(v)
 *   toggleVerifyDialog(v)
 *   openMsgDialog(msg)
 *   showErrorDialog(msg)
 *   showSnackbarMessage(msg, type, dur?, anchor?)
 *   setIsSelfPreparingIndicator(v)
 *   signaturesNeededSet(v)
 *   bankAppAttachedSet(v)
 *   setPreparerID(id)
 *   setAmendedPreparerID(id)
 *   setSuggestedPassword(pwd)
 *   togglePrintDialog(v)
 *   toggleSuggestedPassword(v)
 *   toggleEncryptedPdf(v)
 *   setEncryptedPdfRequest(v)
 *   setEncryptedDocumentContent(v)
 *   setEmailBlob(html)
 *   setRMSflag(v)
 *   inOfficeSignSuccessfulSet(v)
 *   setStateAcks(states)
 *   setAttachedStates(states)
 *   removeDocument(id)
 *   setSentSuccessTitle(t)
 *   setSentSuccessMsg(m)
 *   setEncryptedPDFSuccessDialog(v)
 *   toggleTextLinkSendModalOriginal(v, blob)
 */
const useSocketMessage = ({ calcAPIRef, onMessageRef, socketCallbacksRef }) => {
  useEffect(() => {
    const handleSocketMessage = async wsCommand => {
      const cb = socketCallbacksRef.current;
      const api = calcAPIRef.current;

      cb.setSessionId(wsCommand.sessionid);
      let form = {};
      let formtoLoad = '';
      let response = {};
      let calcres = {};

      try {
        switch (wsCommand.command) {
          case 'invalid_token':
            cb.spinner.stopSpin();
            api?.CloseSession();
            break;
          case 'NOTIFY_USER':
            switch (wsCommand.promptType) {
              case 'SOCKET_START_SPINNER':
                cb.spinner.startSpin(wsCommand.message);
                break;
              case 'SOCKET_STOP_SPINNER':
                cb.spinner.stopSpin();
                break;
              case 'SOCKET_SNACKBAR_ERROR':
                cb.snackbars.showErrorSnackbar(wsCommand.message);
                break;
              case 'SOCKET_MESSAGEBOX':
                break;
            }
            break;
          case 'echo':
            break;
          case 'alert':
            break;
          case 'error': {
            cb.spinner.stopSpin();
            const errorMsg = wsCommand.data ?? 'Unable to complete action';
            cb.snackbars.showErrorSnackbar(errorMsg);
            break;
          }
          case 'textlink_error':
            cb.setTextlinkErrorMessage('');
            if (wsCommand?.data) {
              cb.setTextlinkErrorMessage(wsCommand?.data);
            }
            cb.setShowTextLinkSendErrorDialog(true);
            break;
          case 'form_load': {
            formtoLoad = wsCommand.form;
            form = JSON.parse(trimNull(wsCommand.formdata));
            let currentFormName = '';
            let currentFileName = '';
            if (form.error) {
              cb.showErrorDialog(form.error);
              cb.verify?.resetVerify();
              break;
            }
            if (Object.prototype.hasOwnProperty.call(form, 'arg')) {
              if (form.arg.MSGBOX) {
                store.dispatch(formViewerActions.openMsgDialog(form.arg.MSGBOX));
              }
              currentFormName = form.arg.form + form.arg.occurrence + form.arg.subFrm;
              currentFileName = form.arg.frmName;
              cb.fieldEvents.subFrmRef.current = form.arg.subFrm;
              if (Object.prototype.hasOwnProperty.call(form.arg, 'NavPanelItems')) {
                store.dispatch(
                  returnProfileActions.setActiveReturnFormList(form.arg.NavPanelItems),
                );
              }
              if (Object.prototype.hasOwnProperty.call(form.arg, 'lastRow')) {
                cb.fieldEvents.lastRowRef.current = translateSpecialOccuranceRow(form.arg.lastRow);
              }
              if (Object.prototype.hasOwnProperty.call(form.arg, 'nextFld')) {
                cb.formNav.goToField(cb.formNav.activeForm, form.arg.nextFld);
              } else {
                cb.formNav.scrollFormTo(TopOfFormPage);
              }
              if (Object.prototype.hasOwnProperty.call(form, 'frmSwitch')) {
                let fieldFocus = form.frmSwitch.substr(form.frmSwitch.length - 6);
                fieldFocus += '00';
                const formFocus = form.frmSwitch.substr(0, 6);
                cb.formNav.goToField(formFocus, fieldFocus);
              }
              cb.fieldEvents.dblBlindListRef.current = [];
              if (Object.prototype.hasOwnProperty.call(form.arg, 'fields')) {
                for (const i in form.arg.fields) {
                  const j = form.arg.fields[i];
                  if (j.dblBlind) cb.fieldEvents.dblBlindListRef.current.push(i.substring(0, 4));
                }
              }
              cb.fieldEvents.dblBlindListRef.current.push('bdan');
              form = form.arg;
              if (cb.fieldEvents.maintainAutoExtendFocus && cb.currentElRef?.current.id) {
                cb.formNav.goToField(cb.formNav.activeForm, cb.currentElRef?.current?.id);
              }
            } else {
              if (Object.prototype.hasOwnProperty.call(form, 'NavPanelItems')) {
                store.dispatch(returnProfileActions.setActiveReturnFormList(form.NavPanelItems));
              }
              currentFormName = form.form + form.occurrence + form.subFrm;
              currentFileName = form.frmName;
              cb.fieldEvents.subFrmRef.current = form.subFrm;
            }
            if (cb.formNav.interviewMode) {
              cb.formNav.setTaxForm(
                formtoLoad,
                currentFormName,
                currentFileName,
                cb.fieldEvents.subFrmRef.current,
              );
              cb.formPopulation.UpdateForm(form);
              const currFormElement = cb.fieldEvents.interviewCurrentEleMap.has(
                cb.formNav.activeForm,
              )
                ? cb.fieldEvents.interviewCurrentEleMap.get(cb.formNav.activeForm).id
                : false;
              if (currFormElement) {
                cb.formNav.goToField(cb.formNav.activeForm, currFormElement);
                const lookupField = form.fields[currFormElement];
                const prevLookupField = form.fields[cb.fieldEvents.previousElRef.current.id];
                if (lookupField?.color === '90' && lookupField.value) {
                  cb.fieldEvents.handleReturnFieldCalc(
                    cb.currentElRef?.current.id,
                    cb.currentElRef?.current.value,
                  );
                } else if (prevLookupField?.color === '90' && prevLookupField.value) {
                  cb.fieldEvents.handleReturnFieldCalc(
                    cb.fieldEvents.previousElRef.current.id,
                    cb.fieldEvents.previousElRef.current.value,
                  );
                }
              }
              cb.lockFieldsIfRequired();
            } else {
              cb.formNav.setTaxForm(
                formtoLoad,
                currentFormName,
                currentFileName,
                cb.fieldEvents.subFrmRef.current,
              );
              cb.formPopulation.PopulateForm(form);
              if (
                cb.fieldEvents.maintainAutoExtendFocus &&
                cb.currentElRef?.current.id === cb.fieldEvents.tabbedField
              ) {
                cb.setState({ maintainAutoExtendFocus: false });
                const el = document.getElementById(cb.currentElRef?.current?.id);
                if (el) el.value = cb.fieldEvents.autoExtendTabValue;
                cb.lockFieldsIfRequired();
              }
            }
            if (cb.getState().loadEstimatorState) {
              cb.clearEstimatorState?.();
              cb.setState({ loadEstimatorState: false });
            }
            if (cb.payload?.season >= 2024) {
              const formContainerTarget = document.getElementsByClassName('form-page')[0];
              if (formContainerTarget !== null) {
                const formName = form?.form?.substring(0, 2);
                if (form?.showdraft === 'true' && formName !== 'A0') {
                  formContainerTarget.classList.add('showdraft');
                }
              }
            }
            break;
          }
          case 'return_init': {
            const returnInit = JSON.parse(trimNull(wsCommand.data));
            if (cb.payload?.is_business) {
              store.dispatch(
                formViewerActions.setBusinessElectronicDisclosure(
                  returnInit?.businessElectronicDisclosure,
                ),
              );
            } else {
              store.dispatch(
                formViewerActions.setTaxpayerElectronicDisclosure(
                  returnInit?.taxpayerElectronicDisclosure,
                ),
              );
              store.dispatch(
                formViewerActions.setSpouseElectronicDisclosure(
                  returnInit?.spouseElectronicDisclosure,
                ),
              );
            }
            cb.formPopulation.setColorMap(returnInit.colorMap);
            const calcResponse = JSON.parse(trimNull(returnInit.calcResponse));
            if (calcResponse.arg.frmSwitch && calcResponse.arg.frmSwitch !== '') {
              switch (calcResponse.arg.frmSwitch) {
                case 'stat03':
                  // Navigate to the preparer Notes page. navigateToNotesForm handles
                  // both cases: if not already on retn19 it navigates and fetches the
                  // latest notes via special_form_load; if already there it just stops
                  // the spinner (notes will arrive from return_init below).
                  cb.formNav.navigateToNotesForm();
                  break;
                case 'stat02':
                  cb.formNav.doInitFormLoad();
                  cb.verify.verifyReturn(VERIFY_MODAL_TABS.REJECTS);
                  break;
                case 'stat01':
                case '000000':
                case '0000000000':
                  cb.formNav.doInitFormLoad();
                  break;
                default: {
                  api?.ReturnFormLoad(calcResponse.arg.frmSwitch.substring(0, 6), '', '', '');
                  let field = calcResponse.arg.frmSwitch.substring(6, 10) + '00';
                  let fieldExistCount = 0;
                  const fieldExists = setInterval(() => {
                    if (document.getElementById(field) || fieldExistCount >= 10) {
                      if (
                        field === 'RMST00' &&
                        document.getElementById(field)?.classList.contains('hide') &&
                        document.getElementById('RMST01') &&
                        !document.getElementById('RMST01').classList.contains('hide')
                      ) {
                        field = 'RMST01';
                      }
                      if (document.getElementById(field) != null) {
                        document.getElementById(field).focus();
                        clearInterval(fieldExists);
                      }
                    }
                    if (fieldExistCount > 100) clearInterval(fieldExists);
                    fieldExistCount++;
                  }, 150);
                }
              }
            } else {
              cb.formNav.doInitFormLoad();
            }
            if (calcResponse.arg.notes && calcResponse.arg.notes.length !== 0) {
              cb.bookmarks.setHasNotes(true);
              cb.bookmarks.handlePreparerNotesReq(calcResponse.arg?.notes);
            }
            store.dispatch(
              returnProfileActions.setIsTaxPassReturnFlag(returnInit.taxpassReturnID > 0),
            );
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(calcResponse.arg.NavPanelItems),
            );
            store.dispatch(returnProfileActions.setReadOnlyMode(returnInit?.readOnlyMode || false));
            document.body.classList.add('bodyStyleOverflowHidden');
            if (Object.prototype.hasOwnProperty.call(returnInit, 'locked_by')) {
              store.dispatch(
                returnProfileActions.lockReturn(returnInit.locked_by, returnInit.lockedByLoginID),
              );
            }
            cb.needingVerifiedSet(true);
            cb.returnInitializedRef.current = true;
            api?.SetReturnInitStatus(cb.returnInitializedRef.current);
            if (cb.isSelfPreparing !== returnInit?.isSelfPreparing) {
              cb.setIsSelfPreparingIndicator(returnInit?.isSelfPreparing);
            }
            break;
          }
          case 'return_save':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.error) {
              cb.showErrorDialog(response.error);
              cb.spinner.stopSpin();
              break;
            }
            cb.showSnackbarMessage('Tax Return Saved', 'success', 1500, {
              vertical: 'top',
              horizontal: 'center',
            });
            if (Object.prototype.hasOwnProperty.call(response, 'arg')) {
              if (
                typeof response.arg.MSGBOX === 'undefined' &&
                response.arg.TaxReturnDetails !== undefined
              ) {
                if (response.arg.TaxReturnDetails.Business !== undefined) {
                  store.dispatch(
                    formViewerActions.taxpayerInformationSet({
                      BusinessName: response.arg.TaxReturnDetails.Business.BusinessName,
                      SSN: response.arg.TaxReturnDetails.Business.EIN,
                      email: response.arg.TaxReturnDetails.Business.email,
                      cellPhone: response.arg.TaxReturnDetails.Business.Phone,
                      cellCarrier: response.arg.TaxReturnDetails.Business.CellCarrier,
                      cellDomain: response.arg.TaxReturnDetails.Business.CellDomain,
                      signeeType: SIGNEE_TYPE.TAXPAYER,
                      zipCode: response.arg.StatusRecord.ZipCode,
                    }),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerName(
                      response.arg.TaxReturnDetails.Business.BusinessName,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerEmail(
                      response.arg.TaxReturnDetails.Business.email,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneNumber(
                      response.arg.TaxReturnDetails.Business.Phone,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneCarrier(
                      response.arg.TaxReturnDetails.Business.CellCarrier,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneDomain(
                      response.arg.TaxReturnDetails.Business.CellDomain,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerAllowText(
                      response.arg.TaxReturnDetails.Business.AllowText,
                    ),
                  );
                }
                if (response.arg.TaxReturnDetails.Taxpayer !== undefined) {
                  cb.print.mergeRemoteSignature({
                    tpFirstName: { $set: response.arg.TaxReturnDetails.Taxpayer.FirstName },
                    tpLastName: { $set: response.arg.TaxReturnDetails.Taxpayer.LastName },
                    tpDOB: { $set: response.arg.TaxReturnDetails.Taxpayer.DOB },
                    tpLast4: { $set: response.arg.TaxReturnDetails.Taxpayer.SSN },
                    tpEmail: { $set: response.arg.TaxReturnDetails.Taxpayer.email },
                    tpCellPhone: { $set: response.arg.TaxReturnDetails.Taxpayer.CellPhone },
                    tpCellCarrier: { $set: response.arg.TaxReturnDetails.Taxpayer.CellCarrier },
                    tpCellDomain: { $set: response.arg.TaxReturnDetails.Taxpayer.CellDomain },
                    remoteSignReturnID: { $set: response.rtnId },
                    returnCurrentState: { $set: response.arg },
                  });
                  store.dispatch(
                    formViewerActions.taxpayerInformationSet({
                      firstName: response.arg.TaxReturnDetails.Taxpayer.FirstName,
                      lastName: response.arg.TaxReturnDetails.Taxpayer.LastName,
                      DOB: response.arg.TaxReturnDetails.Taxpayer.DOB,
                      SSN: response.arg.TaxReturnDetails.Taxpayer.SSN,
                      email: response.arg.TaxReturnDetails.Taxpayer.email,
                      cellPhone: response.arg.TaxReturnDetails.Taxpayer.CellPhone,
                      cellCarrier: response.arg.TaxReturnDetails.Taxpayer.CellCarrier,
                      cellDomain: response.arg.TaxReturnDetails.Taxpayer.CellDomain,
                      signeeType: SIGNEE_TYPE.TAXPAYER,
                      zipCode: response.arg.StatusRecord.ZipCode,
                    }),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerName(
                      response.arg.TaxReturnDetails.Taxpayer.FirstName +
                      ' ' +
                      response.arg.TaxReturnDetails.Taxpayer.LastName,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerEmail(
                      response.arg.TaxReturnDetails.Taxpayer.email,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneNumber(
                      response.arg.TaxReturnDetails.Taxpayer.CellPhone,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneCarrier(
                      response.arg.TaxReturnDetails.Taxpayer.CellCarrier,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerCellPhoneDomain(
                      response.arg.TaxReturnDetails.Taxpayer.CellDomain,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setTaxpayerAllowText(
                      response.arg.TaxReturnDetails.Taxpayer.AllowText,
                    ),
                  );
                }
                if (response.arg.TaxReturnDetails.Spouse !== undefined) {
                  cb.print.mergeRemoteSignature({
                    spFirstName: { $set: response.arg.TaxReturnDetails.Spouse.FirstName },
                    spLastName: { $set: response.arg.TaxReturnDetails.Spouse.LastName },
                    spDOB: { $set: response.arg.TaxReturnDetails.Spouse.DOB },
                    spLast4: { $set: response.arg.TaxReturnDetails.Spouse.SSN },
                    spEmail: { $set: response.arg.TaxReturnDetails.Spouse.email },
                    spCellPhone: { $set: response.arg.TaxReturnDetails.Spouse.CellPhone },
                    spCellCarrier: { $set: response.arg.TaxReturnDetails.Spouse.CellCarrier },
                    spCellDomain: { $set: response.arg.TaxReturnDetails.Spouse.CellDomain },
                  });
                  store.dispatch(
                    formViewerActions.spouseInformationSet({
                      firstName: response.arg.TaxReturnDetails.Spouse.FirstName,
                      lastName: response.arg.TaxReturnDetails.Spouse.LastName,
                      DOB: response.arg.TaxReturnDetails.Spouse.DOB,
                      SSN: response.arg.TaxReturnDetails.Spouse.SSN,
                      email: response.arg.TaxReturnDetails.Spouse.email,
                      cellPhone: response.arg.TaxReturnDetails.Spouse.CellPhone,
                      cellCarrier: response.arg.TaxReturnDetails.Spouse.CellCarrier,
                      cellDomain: response.arg.TaxReturnDetails.Spouse.CellDomain,
                      signeeType: SIGNEE_TYPE.SPOUSE,
                      zipCode: response.arg.StatusRecord.ZipCode,
                    }),
                  );
                  store.dispatch(formViewerActions.setSpouseInfoAvailable(true));
                  store.dispatch(
                    formViewerActions.setSpouseName(
                      response.arg.TaxReturnDetails.Spouse.FirstName +
                      ' ' +
                      response.arg.TaxReturnDetails.Spouse.LastName,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setSpouseEmail(response.arg.TaxReturnDetails.Spouse.email),
                  );
                  store.dispatch(
                    formViewerActions.setSpouseCellPhoneNumber(
                      response.arg.TaxReturnDetails.Spouse.CellPhone,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setSpouseCellPhoneCarrier(
                      response.arg.TaxReturnDetails.Spouse.CellCarrier,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setSpouseCellPhoneDomain(
                      response.arg.TaxReturnDetails.Spouse.CellDomain,
                    ),
                  );
                  store.dispatch(
                    formViewerActions.setSpouseAllowText(
                      response.arg.TaxReturnDetails.Spouse.AllowText,
                    ),
                  );
                }
                if (cb.print.remoteSignPrint === true) {
                  cb.print.handleSigningRemotely();
                } else if (cb.print.pendingSignatureModal === true) {
                  cb.print.setPendingSignatureModal(false);
                  store.dispatch(remoteSignActions.sendSignatureModalSet(true));
                  cb.spinner.stopSpin();
                } else if (cb.print.encryptedPdfPrint === true) {
                  cb.toggleSuggestedPassword(true);
                  cb.print.setEncryptedPdfPrint(false);
                  cb.spinner.stopSpin();
                }
                cb.spinner.stopSpin();
              } else {
                if (response.arg.MSGBOX) cb.openMsgDialog(response.arg.MSGBOX);
                cb.spinner.stopSpin();
              }
            } else {
              if (response.arg?.MSGBOX) cb.openMsgDialog(response.arg.MSGBOX);
              cb.spinner.stopSpin();
            }
            cb.setReturnSaved(true);
            break;
          case 'return_auto_save':
            break;
          case 'return_close':
            cb.spinner.stopSpin();
            api?.CloseSession();
            // Clear return-scoped FormViewer state so the next return doesn't
            // inherit stale data (CLO-11608).
            store.dispatch(formViewerActions.resetFormViewer());
            break;
          case 'return_saveclose':
            cb.spinner.stopSpin();
            api?.CloseSession();
            // Clear return-scoped FormViewer state so the next return doesn't
            // inherit stale data (CLO-11608).
            store.dispatch(formViewerActions.resetFormViewer());
            break;
          case 'return_openpdfattach':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(formViewerActions.setDocumentsAttach(response));
            cb.openAddAttachmentModal();
            break;
          case 'return_openVisionAssist':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.disable_tva) {
              cb.setOpenTVADisallow(true);
            } else {
              const TVAargType = String(response.type).replace(/^0+/, '') ?? '';
              if (!String(TVAargType).match(/^([1-9]|10)$/)) {
                cb.showErrorDialog(
                  'Service is currently unavailable. Please contact technical support for assistance',
                );
                break;
              }
              cb.setTVAargType(TVAargType);
              cb.setIsVisionModalOpen(true);
            }
            break;
          case 'return_buttonpress': {
            response = JSON.parse(trimNull(wsCommand.data));
            const msgbox = response.arg.MSGBOX;
            if (!msgbox) {
              const cmd = response.arg.menuCmd;
              if (!cmd) throw ErrorHelpers.createSimpleError('Unable to complete action');
              cb.handleMenuCommand(cmd, response);
              return;
            }
            store.dispatch(formViewerActions.openMsgDialog(msgbox));
            break;
          }
          case 'return_msgresp':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.escCmd) {
              if (response.escCmd === 'X') cb.restorePreviousValue();
            } else if (response.errorPop) {
              cb.restorePreviousValue();
              cb.showCalcError(response.title, response.error);
            } else if (response.arg?.visionAssistContinued) {
              if (response.arg?.formLoad) {
                cb.setTVAprogress(100);
                setTimeout(() => {
                  cb.setIsVisionModalOpen(false);
                  cb.setTVAprogress(0);
                }, 500);
                api?.ReturnFormLoad(response.arg.formLoad, '', '', '');
              } else {
                cb.setTVAprogress(100);
                cb.formPopulation.UpdateForm(response.arg);
                setTimeout(() => {
                  cb.setIsVisionModalOpen(false);
                  cb.setTVAprogress(0);
                }, 500);
              }
            } else if (response?.arg) {
              if (response.arg?.MSGBOX)
                store.dispatch(formViewerActions.openMsgDialog(response.arg.MSGBOX));
              if (response.arg?.PDF) cb.print.displayPrint(response.arg.PDF?.pdfBLOB);
              if (response.arg?.formLoad) {
                api?.ReturnFormLoad(response.arg.formLoad, '', '', '');
              } else if (response.arg?.fields) {
                if (response.arg.fields?.RMST00?.value) {
                  store.dispatch(
                    formViewerActions.setTaxpayerElectronicDisclosure(
                      response.arg.fields.RMST00.value,
                    ),
                  );
                }
                if (response.arg.fields?.RMSS00?.value) {
                  store.dispatch(
                    formViewerActions.setSpouseElectronicDisclosure(
                      response.arg.fields.RMSS00.value,
                    ),
                  );
                }
                if (response.arg.fields?.RMSQ00?.value) {
                  store.dispatch(
                    formViewerActions.setBusinessElectronicDisclosure(
                      response.arg.fields.RMSQ00.value,
                    ),
                  );
                }
                cb.formPopulation.UpdateForm(response.arg);
              }
            }
            cb.needingVerifiedSet(true);
            break;
          case 'return_newasset':
            response = JSON.parse(trimNull(wsCommand.data));
            if (typeof response.error !== 'undefined') cb.showErrorDialog(response.error);
            cb.needingVerifiedSet(true);
            break;
          case 'return_verify':
            response = JSON.parse(wsCommand.data);
            store.dispatch(returnProfileActions.setAllVerifiesAndErrors(response.arg));
            if (cb.verify.verifyType === 'queueReturn') {
              cb.openErrorRejectModal();
              if (response.arg.errors === 0 && response.arg.warnings === 0) {
                cb.toggleVerifyModalDialog(true);
              }
            } else if (
              cb.verify.verifyType === 'verify' &&
              response.arg.errors === 0 &&
              response.arg.warnings === 0
            ) {
              cb.toggleVerifyDialog(true);
              cb.setVerifyType('');
            } else {
              store.dispatch(formViewerActions.setRejectTab(null));
              cb.openErrorRejectModal();
            }
            cb.spinner.stopSpin();
            break;
          case 'update_form': {
            cb.needingVerifiedSet(true);
            form = JSON.parse(trimNull(wsCommand.data));
            const currentForm = cb.formNav.activeForm;
            if (!form.error) {
              if (form.arg.MSGBOX) store.dispatch(formViewerActions.openMsgDialog(form.arg.MSGBOX));
              cb.formPopulation.UpdateForm(form.arg);
              const selfPrepField1040 = form?.arg?.fields?.SELF00;
              const selfPrepFieldCorp = form?.arg?.fields?.['847000'];
              let isSelfPreparing = false;
              let shouldUpdateSelfPrep = false;
              if (selfPrepFieldCorp !== undefined && currentForm === '000A00') {
                isSelfPreparing = selfPrepFieldCorp?.value === 'X';
                shouldUpdateSelfPrep = true;
              } else if (
                selfPrepField1040 !== undefined &&
                (currentForm === 'US0101' || currentForm === 'US9901')
              ) {
                isSelfPreparing = selfPrepField1040?.value === 'S';
                shouldUpdateSelfPrep = true;
              }
              if (shouldUpdateSelfPrep && cb.isSelfPreparing !== isSelfPreparing)
                cb.setIsSelfPreparingIndicator(isSelfPreparing);
              if (form?.arg?.fields?.RMST00?.value) {
                store.dispatch(
                  formViewerActions.setTaxpayerElectronicDisclosure(form.arg.fields.RMST00.value),
                );
              }
              if (form?.arg?.fields?.RMSS00?.value) {
                store.dispatch(
                  formViewerActions.setSpouseElectronicDisclosure(form.arg.fields.RMSS00.value),
                );
              }
              if (form?.arg?.fields?.RMSQ00?.value) {
                store.dispatch(
                  formViewerActions.setBusinessElectronicDisclosure(form.arg.fields.RMSQ00.value),
                );
              }
              if (form.arg.NavPanelItems) {
                store.dispatch(
                  returnProfileActions.setActiveReturnFormList(form.arg.NavPanelItems),
                );
              }
              if (cb.verify.verifyDirty) {
                cb.verify.setVerifyDirty(false);
                cb.verify.verifyReturn(1);
              } else if (cb.currentElRef?.current) {
                const focused = document.activeElement;
                if (form.arg && form.arg.nextFld !== focused.id && form.arg.nextFld !== '') {
                  cb.formNav.goToField(cb.formNav.activeForm, form.arg.nextFld);
                }
              }
              const { command } = form;
              if (command === 'return_yty' && cb.startInterview && !cb.payload?.is_business) {
                cb.interviewMode();
              } else if (command === 'return_yty') {
                api?.ReturnFormLoad('000A00', '', '', '');
              }
            }
            break;
          }
          case 'return_choicelist':
            response = JSON.parse(wsCommand.data);
            if (response?.arg?.ExplicitType?.Window?.Title === 'State Package') {
              const sortedData = cb.print.sortAvailablePackages(response.arg.ExplicitType?.Data);
              response.arg.ExplicitType.Data = sortedData;
            }
            store.dispatch(formViewerActions.setChoiceList(response.arg.ExplicitType));
            cb.fieldEvents.initAutocomplete(response.arg.ExplicitType);
            break;
          case 'return_choicelist_calc':
            response = JSON.parse(wsCommand.data);
            // Choice-list selections (e.g. asset class) recalc dependent fields and
            // button eligibility server-side; without this the DOM/button state goes
            // stale until the next form_load.
            if (response.arg?.fields) {
              cb.formPopulation.UpdateForm(response.arg);
            }
            cb.needingVerifiedSet(true);
            break;
          case 'return_formslist': {
            const strippedJSON = wsCommand?.data?.replaceAll('\t', '')?.replaceAll('\n', '');
            response = JSON.parse(strippedJSON);
            if (response.arg.forms) {
              let formsAsReceived = response.arg.forms;
              if (cb.addFormPkg === 'US' && !cb.payload?.is_business) {
                const indexOfW2 = formsAsReceived.findIndex(i => i.formId === 'US02');
                if (indexOfW2 !== 1) {
                  formsAsReceived = formsAsReceived.splice(indexOfW2, 1).concat(formsAsReceived);
                }
              }
              store.dispatch(formViewerActions.setAddFormList(formsAsReceived));
              await cb.PopulateFormsAndActivitiesList(formsAsReceived);
              store.dispatch(formViewerActions.setSelectedFormToAdd(formsAsReceived[0]));
            }
            if (response.arg.activities) {
              store.dispatch(formViewerActions.setActivityList(response.arg.activities));
              await cb.PopulateFormsAndActivitiesList(response.arg.activities);
              store.dispatch(formViewerActions.setSelectedFormToAdd(response.arg.activities[0]));
            }
            break;
          }
          case 'return_listk1s':
            response = JSON.parse(wsCommand.data);

            if (response.arg.k1List) {
              cb.setK1List(response.arg.k1List);
              cb.setK1pkgList(response.arg.stateList);
              cb.setHideK1Flag(response.arg.hideK1Flag === '1');
            }
            break;
          case 'return_addk1':
            response = JSON.parse(wsCommand.data);
            if (response.arg.NavPanelItems) {
              store.dispatch(
                returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
              );
            }
            if (response.arg.formOccur) api?.ReturnFormLoad(response.arg.formOccur, '', '', '');
            break;
          case 'return_deletek1':
            response = JSON.parse(wsCommand.data);
            if (response.arg.k1List) cb.setK1List(response.arg.k1List);
            if (response.arg.NavPanelItems) {
              store.dispatch(
                returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
              );
            }
            break;
          case 'return_hidek1s':
            response = JSON.parse(wsCommand.data);
            if (response.arg.NavPanelItems) {
              store.dispatch(
                returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
              );
            }
            break;
          case 'return_emailk1':
            response = JSON.parse(wsCommand.data);
            if (response.arg.emailBlob && response.arg.emailBody && response.arg.sendTo) {
              XlinkAPI.emailEncryptedDoc(
                response.arg.sendTo,
                '',
                '',
                'Schedule K1',
                '',
                response.arg.emailBody,
                response.arg.emailBlob,
                cb.getPreparerID(),
                true,
                false,
                false,
                true,
                true,
                true,
              )
                .then(() => {
                  cb.toggleEncryptedPdf(false);
                  cb.setSentSuccessTitle('Schedule K1 Sent');
                  cb.setSentSuccessMsg('The attached document has been sent successfully');
                  cb.setEncryptedPDFSuccessDialog(true);
                  cb.print.setShowFinalPDF(true);
                })
                .catch(error => ErrorHelpers.handleError('Error Sending Schedule K1 PDF.', error));
            }
            break;
          case 'return_deleteasset':
            response = JSON.parse(wsCommand.data);
            if (response.arg.activities)
              store.dispatch(formViewerActions.setActivityList(response.arg.activities));
            cb.toggleAssetSnackbar('Asset Deleted', true);
            cb.needingVerifiedSet(true);
            break;
          case 'return_reassignassetlist':
            response = JSON.parse(wsCommand.data);
            if (response.arg.activities) cb.setReassignAssetList(response.arg.activities);
            cb.needingVerifiedSet(true);
            break;
          case 'return_reassignasset':
            response = JSON.parse(wsCommand.data);
            if (response.arg.activities)
              store.dispatch(formViewerActions.setActivityList(response.arg.activities));
            cb.toggleAssetSnackbar('Asset Assigned', true);
            cb.needingVerifiedSet(true);
            break;
          case 'return_addform':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
            );
            cb.needingVerifiedSet(true);
            break;
          case 'return_removeform':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
            );
            cb.needingVerifiedSet(true);
            break;
          case 'return_removepackage':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
            );
            cb.needingVerifiedSet(true);
            break;
          case 'return_prompt_prioryear': {
            const promptPrior = JSON.parse(trimNull(wsCommand.data));
            cb.formPopulation.setColorMap(promptPrior.colorMap);
            response = JSON.parse(trimNull(promptPrior.calcResponse));
            store.dispatch(formViewerActions.setYTYReturns(response.pyReturns, response.cyReturns));
            cb.primarySSNEIN !== '999999999'
              ? store.dispatch(formViewerActions.openYTYDialog())
              : api?.ReturnFormLoad('000A00', '', '', '');
            if (Object.prototype.hasOwnProperty.call(promptPrior, 'locked_by')) {
              store.dispatch(
                returnProfileActions.lockReturn(promptPrior.locked_by, promptPrior.lockedByLoginID),
              );
            }
            break;
          }
          case 'return_prioryear_info': {
            const prioryearInfo = JSON.parse(trimNull(wsCommand.data));
            cb.formPopulation.setColorMap(prioryearInfo.colorMap);
            response = JSON.parse(trimNull(prioryearInfo.calcResponse));
            store.dispatch(formViewerActions.setYTYReturns(response.pyReturns, response.cyReturns));
            switch (response?.frmSwitch) {
              case 'stat03':
                cb.formNav.navigateToNotesForm();
                break;
              default:
                cb.formNav.doInitFormLoad();
            }
            break;
          }
          case 'return_profile':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
            );
            break;
          case 'load_list':
            break;
          case 'get_statement':
            break;
          case 'return_print_query':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.error) {
              cb.showErrorDialog(response.error);
              cb.spinner.stopSpin();
              break;
            }
            cb.spinner.stopSpin();
            if (Object.prototype.hasOwnProperty.call(response, 'arg')) {
              const cmd = response.arg.menuCmd;
              if (cmd) {
                cb.handleMenuCommand(cmd, response);
                break;
              }
              if (!response.arg.MSGBOX) {
                cb.signaturesNeededSet(response.arg.signquery);
                cb.bankAppAttachedSet(!!response.arg.bankDoc);
              }
              cb.print.mergePrint({
                reqSigs: { $set: response.arg.signquery },
                originalSignquery: { $set: response.arg.signquery },
                ppSignature: { $set: response.arg.pp },
                efinSignature: { $set: response.arg.efin },
                ppxSignature: { $set: response.arg.ppx },
                pActionEligible: { $set: response.arg.pActionEligible },
                cltr: { $set: response.arg.cltr },
                consent: { $set: response.arg.sign_consent },
              });
              cb.setPreparerID(response.arg.pp);
              cb.setAmendedPreparerID(response.arg.ppx);
              cb.setBankID(response.arg.bankDoc);
              if (response.arg.suggestedPwd !== undefined)
                cb.setSuggestedPassword(response.arg.suggestedPwd);
              !cb.isSignatureRequestActive && cb.togglePrintDialog(!response.arg.MSGBOX && true);
              if (response.arg.MSGBOX) cb.openMsgDialog(response.arg.MSGBOX);
            }
            cb.print.setReturnPrintQueryLoading(false);
            break;
          case 'return_print':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.error) {
              cb.showErrorDialog(response.error);
              cb.spinner.stopSpin();
              break;
            }
            !cb.isSignatureRequestActive && cb.spinner.stopSpin();
            if (Object.prototype.hasOwnProperty.call(response, 'arg')) {
              const { PDF, emailBlob, MSGBOX, NavPanelItems } = { ...response.arg };
              const { pdfBLOB, pdfBLOBEnc, GUID, msgBlob } = { ...(PDF || {}) };
              if (PDF) {
                if (emailBlob) {
                  try {
                    const receivedEmailBlob = '{' + atob(emailBlob) + '}';
                    rtfToHTML.fromString(receivedEmailBlob, (_err, html) => cb.setEmailBlob(html));
                  } catch (err) {
                    cb.setEmailBlob('');
                  }
                } else {
                  cb.setEmailBlob('');
                }
                cb.setEncryptedDocumentContent(pdfBLOBEnc !== '' ? pdfBLOBEnc : msgBlob);
                if (cb.print.showFinalPDF === true) {
                  if (!cb.isRMSSigningMethod) cb.print.displayPrint(pdfBLOB);
                  cb.setRMSflag(false);
                  cb.getLatestArchivedDocuments();
                  cb.inOfficeSignSuccessfulSet(true);
                } else {
                  cb.getLatestArchivedDocuments();
                  if (cb.isSignatureRequestActive && MSGBOX === undefined) {
                    sendSignatureRequest(
                      cb.returnID,
                      cb.print.print.reqSigs,
                      pdfBLOB,
                      GUID,
                      msgBlob,
                      cb.signatureRequestData,
                      cb.eroData,
                      cb.preparerData,
                      cb.amendedPreparerData,
                      cb.print.handleSigningInOffice,
                      cb.getBankID(),
                      cb.initialSigneeData?.signatureDocID,
                      cb.payload?.season,
                      () => cb.spinner.stopSpin(),
                      cb.payload?.preparer_info.preparer_id,
                    );
                  }
                  if (!cb.encryptedPdfSelected) {
                    cb.print.setShowFinalPDF(true);
                  } else {
                    cb.setEncryptedPdfRequest(false);
                    cb.toggleEncryptedPdf(true);
                  }
                }
              }
              if (MSGBOX !== undefined) {
                cb.openMsgDialog(response.arg.MSGBOX);
              } else {
                store.dispatch(returnProfileActions.setActiveReturnFormList(NavPanelItems));
              }
              cb.inOfficeSignSuccessfulSet(false);
              cb.spinner.stopSpin();
            }
            break;
          case 'start_spin':
            cb.spinner.startSpin();
            break;
          case 'stop_spin':
            cb.spinner.stopSpin();
            break;
          case 'invalid_request':
            console.log('Invalid Request with data: ' + wsCommand.data);
            break;
          case 'special_form_load':
            cb.handleSpecialFormLoadResponse(JSON.parse(trimNull(wsCommand.data)));
            cb.spinner.stopSpin();
            break;
          case 'return_add_document':
            response = JSON.parse(trimNull(wsCommand.data));
            if (Object.prototype.hasOwnProperty.call(response, 'calcres')) {
              calcres = JSON.parse(trimNull(response.calcres));
              store.dispatch(
                returnProfileActions.setActiveReturnFormList(calcres.arg.NavPanelItems),
              );
            } else {
              store.dispatch(
                returnProfileActions.addDocument(
                  response.document_id,
                  response.document_type,
                  response.document_name,
                  response.document_description,
                  response.create_date,
                  response.document_status_var,
                  response.document_media_type,
                ),
              );
            }
            cb.showSnackbarMessage('Document has been saved.', 'success', 2500, {
              vertical: 'top',
              horizontal: 'center',
            });
            cb.getLatestArchivedDocuments();
            break;
          case 'return_remove_document':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.removeDocument(response.document_id.toString());
            cb.getLatestArchivedDocuments();
            break;
          case 'return_document_detach':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.detachDocument(
                response.document_id,
                response.document_status_var,
              ),
            );
            calcres = JSON.parse(trimNull(response.calcres));
            store.dispatch(returnProfileActions.setActiveReturnFormList(calcres.arg.NavPanelItems));
            break;
          case 'return_stateacks':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.spinner.stopSpin();
            if (typeof response.arg.MSGBOX === 'undefined') {
              cb.setStateAcks(response.arg.States);
              cb.print.openPrintStateAcksModal(PRINT_TYPES.STATE_ACK_LETTER);
            } else {
              cb.openMsgDialog(response.arg.MSGBOX);
            }
            break;
          case 'return_amended_stateacks':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.spinner.stopSpin();
            if (typeof response.arg.MSGBOX === 'undefined') {
              cb.setStateAcks(response.arg.States);
              cb.print.openPrintStateAcksModal(PRINT_TYPES.STATE_AMENDED_ACK_LETTER);
            } else {
              cb.openMsgDialog(response.arg.MSGBOX);
            }
            break;
          case 'return_extension_stateacks':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.spinner.stopSpin();
            if (typeof response.arg.MSGBOX === 'undefined') {
              cb.setStateAcks(response.arg.States);
              cb.print.openPrintStateAcksModal(PRINT_TYPES.STATE_EXT_ACK_LETTER);
            } else {
              cb.openMsgDialog(response.arg.MSGBOX);
            }
            break;
          case 'return_attachedstates':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.spinner.stopSpin();
            if (typeof response.arg.MSGBOX === 'undefined') {
              cb.setAttachedStates(response.arg.States);
              cb.print.openPrintStateClientLetters();
            } else {
              cb.openMsgDialog(response.arg.MSGBOX);
            }
            break;
          case 'return_xmitlist':
            response = JSON.parse(trimNull(wsCommand.data));
            if (Object.prototype.hasOwnProperty.call(response, 'arg')) {
              const cmd = response.arg.menuCmd;
              if (cmd) {
                cb.handleMenuCommand(cmd, response);
              } else if (response.arg.MSGBOX) {
                cb.openMsgDialog(response.arg.MSGBOX);
              } else {
                cb.setQueueData({
                  transmitItemList: response.arg.XmitListItems,
                  feeItemList: response.arg?.FeeListItems || [],
                  feeTotal: response.arg?.FeeTotal || '',
                  walletBalance: response.arg?.WalletBalance || '',
                  wndwNote: response.arg?.WndwNote || '',
                });
              }
            }
            break;
          case 'return_tvasend':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.setTVAprogress(25);
            if (response.arg?.MSGBOX) {
              cb.setIsVisionModalOpen(false);
              cb.setTVAprogress(0);
              cb.openMsgDialog(response.arg.MSGBOX);
            } else {
              setTimeout(() => api?.TVARetreive(response.arg.tvaID), 1000);
            }
            break;
          case 'return_tvaretrieve': {
            response = JSON.parse(trimNull(wsCommand.data));
            cb.setTVAprogress(50);
            const tvaCount = cb.getTVARetrieveCount();
            if (response.arg?.MSGBOX) {
              cb.setIsVisionModalOpen(false);
              cb.setTVAprogress(0);
              cb.setTVARetrieveCount(0);
              cb.openMsgDialog(response.arg.MSGBOX);
            } else if (tvaCount > 10) {
              cb.showErrorDialog(
                'Service is currently unavailable. Please contact technical support for assistance',
              );
              cb.setIsVisionModalOpen(false);
              cb.setTVAprogress(0);
              cb.setTVARetrieveCount(0);
            } else {
              setTimeout(() => {
                cb.setTVARetrieveCount(tvaCount + 1);
                api?.TVARetreive(response.arg.tvaID);
              }, 3000);
            }
            break;
          }
          case 'return_tvaupdate':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.setTVAprogress(75);
            if (response.arg?.MSGBOX) {
              if (response.arg.MSGBOX?.Btns.length === 1) {
                cb.setIsVisionModalOpen(false);
                cb.setTVAprogress(0);
                cb.setTVARetrieveCount(0);
              } else if (response.arg.MSGBOX?.Title === 'Override Message') {
                //override warning, ignore msgbox and proceed
                cb.setTVAprogress(100);
                setTimeout(() => {
                  cb.setIsVisionModalOpen(false);
                  cb.setTVAprogress(0);
                  cb.setTVARetrieveCount(0);
                  api?.ReturnFormLoad(response.arg.formLoad, '', '', '');
                }, 500);
                break;
              } else {
                cb.setTvaObject(response.arg?.tvaObject);
                cb.setTvaID(response.arg?.tvaID);
                cb.setTVARetrieveCount(0);
              }
              cb.openMsgDialog(response.arg.MSGBOX);
            } else if (response.arg?.formLoad) {
              cb.setTVAprogress(100);
              setTimeout(() => {
                cb.setIsVisionModalOpen(false);
                cb.setTVAprogress(0);
                cb.setTVARetrieveCount(0);
              }, 500);
              api?.ReturnFormLoad(response.arg.formLoad, '', '', '');
            } else if (cb.formNav.interviewMode) {
              cb.setTVAprogress(100);
              setTimeout(() => {
                cb.setIsVisionModalOpen(false);
                cb.setTVAprogress(0);
                cb.setTVARetrieveCount(0);
              }, 500);
              api?.ReturnFormLoad(cb.formNav.activeForm, '', '', '');
            } else {
              cb.setTVAprogress(100);
              cb.formPopulation.UpdateForm(response.arg);
              setTimeout(() => {
                cb.setIsVisionModalOpen(false);
                cb.setTVAprogress(0);
                cb.setTVARetrieveCount(0);
              }, 500);
            }
            break;
          case 'return_tvahelp':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.arg?.link) window.open(response.arg.link);
            break;
          case 'return_xmitqueue':
            if (wsCommand.data === '') {
              cb.showSnackbarMessage('Tax Return Queued For Transmission', 'info');
              cb.closeQueueReturnModal();
              cb.returnClose.closeAndSaveReturn();
              break;
            }
            response = JSON.parse(trimNull(wsCommand.data));
            if (response?.arg?.MSGBOX)
              store.dispatch(formViewerActions.openMsgDialog(response.arg.MSGBOX));
            break;
          case 'submit_for_review':
            cb.showSnackbarMessage(
              `Tax Return ${cb.getReadyForReview() ? 'submitted for' : 'retracted from'} review`,
              'info',
            );
            break;
          case 'return_bill_reload':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.formNav.getTaxForm(cb.formNav.activeForm, '', cb.formNav.activeFile, '');
            break;
          case 'return_stockImport':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.error) {
              cb.setImportStockMsg(response.error);
              cb.setBtnClickSpinner(false);
            } else {
              cb.closeImportStock();
              cb.setBtnClickSpinner(false);
              cb.formNav.getTaxForm(cb.formNav.activeForm, '', cb.formNav.activeFile, '');
            }
            break;
          case 'return_form_error':
            try {
              response = JSON.parse(trimNull(wsCommand.data));
              if (response.error && response.initialField) {
                cb.onUpdateFormError(response);
              } else if (
                response.error.includes(
                  'Another return with the same SSN is already using a filing status of ',
                )
              ) {
                cb.handleFilingStatusError(response.error);
              } else if (response.error) {
                cb.snackbars.showErrorSnackbar(response.error);
              } else {
                cb.snackbars.showErrorSnackbar('Return form error');
              }
            } catch (error) {
              ErrorHelpers.handleError('Unable to complete action', error);
            }
            cb.spinner.stopSpin();
            break;
          case 'return_request_acknowledged':
            console.log('Request Acknowledged');
            break;
          case 'return_open_hyperlink':
            window.open(wsCommand.data);
            break;
          case 'queueReturn':
            cb.handleQueueReturn();
            break;
          case 'printFinal':
            cb.handlePrintFinal();
            break;
          case 'verifyReturn':
            cb.verify.verifyReturn(VERIFY_MODAL_TABS.VERIFY);
            break;
          case 'return_payments_query':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.setReturnPaymentInfo(response.arg);
            cb.spinner.stopSpin();
            break;
          case 'return_payments':
            cb.showSnackbarMessage('Payment Details Saved', 'success');
            break;
          case 'return_bookmark_query':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.arg.bookmarks.length === 0) {
              cb.bookmarks.setBookmarkFldEditor(true);
              return;
            }
            cb.bookmarks.handleBookmarkField(response.arg.bookmarks);
            break;
          case 'return_bookmark_upsert':
            response = JSON.parse(trimNull(wsCommand.data));
            store.dispatch(
              returnProfileActions.setActiveReturnFormList(response.arg.NavPanelItems),
            );
            break;
          case 'return_toggle_status_lock':
            cb.toggleFieldLock(!cb.lockedStatus);
            try {
              await XlinkAPI.setReturnLockStatus(cb.returnID, cb.lockedStatus);
            } catch (err) {
              console.log('Error with locking/unlocking return: ', err);
            }
            cb.spinner.stopSpin();
            break;
          case 'return_notes_upsert':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.bookmarks.handlePreparerNotesReq(response.arg.notes);
            cb.showSnackbarMessage('Note updated', 'success');
            break;
          case 'return_notes_delete':
            response = JSON.parse(trimNull(wsCommand.data));
            cb.bookmarks.handlePreparerNotesReq(response.arg.notes);
            cb.showSnackbarMessage('Note deleted', 'success');
            break;
          case 'return_copy_to_training_dupe_check':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.dupessn) {
              cb.setDupeSSNCopyRtnOpen(true);
            } else {
              cb.copyToTraining();
            }
            break;
          case 'return_copy_to_live_dupe_check':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.dupessn) {
              cb.setDupeSSNCopyLiveRtnOpen(true);
            } else {
              cb.copyToLive();
            }
            break;
          case 'send_textlink_message':
            if (
              (cb.taxpayerChecked ||
                cb.taxpayerEPasshecked ||
                cb.spouseChecked ||
                cb.spouseEPassChecked) === false
            ) {
              store.dispatch(formViewerActions.toggleTextlinkConfirmSentDlg(true));
            }
            break;
          case 'payjunction_terminal_status':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response.status) cb.setPaymentTerminalStatus(response);
            break;
          case 'get_sms_message':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response?.arg?.txtSmsBlob) {
              cb.toggleTextLinkSendModalOriginal(true, response.arg.txtSmsBlob);
            } else {
              console.log('Missing text body client letter');
            }
            break;
          case 'return_linkage':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response && response.arg) cb.setFieldFormLinks(response.arg);
            break;
          case 'return_linkPick':
            response = JSON.parse(trimNull(wsCommand.data));
            if (response?.arg?.navTo) api?.ReturnFormLoad(response.arg.navTo, '', '', '');
            break;
          case 'return_restricted_field':
            form = JSON.parse(trimNull(wsCommand.data));
            if (Object.prototype.hasOwnProperty.call(form, 'arg'))
              cb.updateRestrictedFields(form.arg);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('error in on socket response', error);
        if (ENVIRONMENT === 'local' || ENVIRONMENT === 'development') {
          cb.showErrorDialog('Internal CLO Error: Please check console for error details');
        }
        const wsCommandStr = JSON.stringify(wsCommand);
        if (error.stack) {
          api?.ReturnRaiseError(wsCommandStr, error.toString(), error.stack.toString());
        } else if (error.response) {
          api?.ReturnRaiseError(
            wsCommandStr,
            error.response.data.toString(),
            error.response.toString(),
          );
        }
      }

      cb.lockFieldsIfRequired();
    };

    onMessageRef.current = handleSocketMessage;
  }, []); // reads from refs at call-time — deps intentionally empty
};

export default useSocketMessage;
